/**
 * Central SEO config. Absolute URLs use SITE_URL (VITE_SITE_URL when set,
 * otherwise the production domain below) — see docs/SEO.md.
 */

import { getProductDetail } from "./productDetails.js";
import { getInsightDetail } from "./insightDetails.js";

export const DEFAULT_SITE_URL = "https://www.taldel.com";

export const OG_IMAGE_PATH = "og-default.png";

/** @typedef {"organization" | "service"} SeoJsonLdKind */

/**
 * @typedef {object} SeoPage
 * @property {string} id
 * @property {string} title
 * @property {string} description
 * @property {string} canonicalPath - path after site base, e.g. "/" or "/services/web-development"
 * @property {string} h1 - crawlable heading for prerender
 * @property {string} [ogImage]
 * @property {SeoJsonLdKind[]} jsonLd
 * @property {string | null} [serviceSlug]
 */

/**
 * Resolve site origin+base with no trailing slash.
 * @returns {string}
 */
export function getSiteUrl() {
  const fromEnv =
    typeof import.meta !== "undefined" && import.meta.env?.VITE_SITE_URL
      ? String(import.meta.env.VITE_SITE_URL).trim()
      : "";
  return (fromEnv || DEFAULT_SITE_URL).replace(/\/+$/, "");
}

/**
 * @param {string} canonicalPath
 * @param {string} [siteUrl]
 */
export function absoluteUrl(canonicalPath = "/", siteUrl = getSiteUrl()) {
  const base = siteUrl.replace(/\/+$/, "");
  if (!canonicalPath || canonicalPath === "/") return `${base}/`;
  const path = canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`;
  return `${base}${path}`;
}

/**
 * @param {string} [file]
 * @param {string} [siteUrl]
 */
export function absoluteAssetUrl(file = OG_IMAGE_PATH, siteUrl = getSiteUrl()) {
  const clean = String(file || OG_IMAGE_PATH).replace(/^\/+/, "");
  return `${siteUrl.replace(/\/+$/, "")}/${clean}`;
}

/** @type {SeoPage[]} */
export const seoPages = [
  {
    id: "home",
    title: "Taldel — Offshore Engineering Teams",
    description:
      "Taldel helps startups and growing businesses design, build, and scale software and AI-enabled products — with dedicated offshore engineering teams, AI-vetted and ready to deploy in days.",
    canonicalPath: "/",
    h1: "Full-stack technology partner for startups and scaling businesses.",
    ogImage: OG_IMAGE_PATH,
    jsonLd: ["organization"],
    serviceSlug: null,
  },
  {
    id: "web-development",
    title: "Taldel — Web Development",
    description:
      "Design and ship modern web apps with AI-vetted frontend and full-stack engineers — from MVPs to complex SaaS platforms.",
    canonicalPath: "/services/web-development",
    h1: "Web products built to scale.",
    ogImage: OG_IMAGE_PATH,
    jsonLd: ["service"],
    serviceSlug: "web-development",
  },
  {
    id: "ai-agentic",
    title: "Taldel — Agentic AI",
    description:
      "Design and ship AI agents that plan, tool-call, and execute real workflows — with evals, guardrails, and production observability.",
    canonicalPath: "/services/ai-agentic",
    h1: "Agentic systems that take action.",
    ogImage: OG_IMAGE_PATH,
    jsonLd: ["service"],
    serviceSlug: "ai-agentic",
  },
  {
    id: "app-development",
    title: "Taldel — App Development",
    description:
      "Build web and mobile apps from MVP to scale — auth, billing, admin, analytics, and product loops that retain.",
    canonicalPath: "/services/app-development",
    h1: "Apps built to scale.",
    ogImage: OG_IMAGE_PATH,
    jsonLd: ["service"],
    serviceSlug: "app-development",
  },
  {
    id: "ui-ux-design",
    title: "Taldel — UI/UX Design",
    description:
      "Product design, UX flows, and design systems that make complex software simple — ready for engineering to ship.",
    canonicalPath: "/services/ui-ux-design",
    h1: "Interfaces users actually love.",
    ogImage: OG_IMAGE_PATH,
    jsonLd: ["service"],
    serviceSlug: "ui-ux-design",
  },
  {
    id: "cyber-security",
    title: "Taldel — Cyber Security",
    description:
      "Secure products, identities, and cloud estates — threat modeling, hardening, and detection that ships with the roadmap.",
    canonicalPath: "/services/cyber-security",
    h1: "Security built into the product, not bolted on.",
    ogImage: OG_IMAGE_PATH,
    jsonLd: ["service"],
    serviceSlug: "cyber-security",
  },
  {
    id: "staff-augmentation",
    title: "Taldel — Staff Augmentation",
    description:
      "Embed AI-vetted engineers into your roadmap — clear ownership, timezone overlap, and flexible scale.",
    canonicalPath: "/services/staff-augmentation",
    h1: "Extend your team without the hiring drag.",
    ogImage: OG_IMAGE_PATH,
    jsonLd: ["service"],
    serviceSlug: "staff-augmentation",
  },
  {
    id: "product",
    title: "Taldel — Our Portfolios",
    description:
      "See how Taldel delivers across web, AI, apps, design, cyber security, and staffing — with the same craft in every project.",
    canonicalPath: "/our-portfolios",
    h1: "Portfolios built to ship and scale.",
    ogImage: OG_IMAGE_PATH,
    jsonLd: ["organization"],
    serviceSlug: null,
  },
    {
    id: "insights",
    title: "Taldel — Insights",
    description:
      "Short reads across web, AI, apps, design, cyber security, and staffing. Filter by service, or browse everything.",
    canonicalPath: "/insights",
    h1: "Field notes from delivery, not decks.",
    ogImage: OG_IMAGE_PATH,
    jsonLd: ["organization"],
    serviceSlug: null,
  },
  {
    id: "about",
    title: "Taldel — About Us",
    description:
      "We started Taldel because most software teams are slowed down by process, not talent. So we built a leaner model — offshore engineers who plug directly into your roadmap, vetted by AI, accountable like an in-house team.",
    canonicalPath: "/about-us",
    h1: "We build teams that ship, not slides.",
    ogImage: OG_IMAGE_PATH,
    jsonLd: ["organization"],
    serviceSlug: null,
  },
  {
    id: "signup",
    title: "Taldel — Sign up",
    description:
      "Create a Taldel account as a client, team, or individual. Frontend registration flow with role-specific details.",
    canonicalPath: "/signup",
    h1: "Create your Taldel account.",
    ogImage: OG_IMAGE_PATH,
    jsonLd: ["organization"],
    serviceSlug: null,
  },
  {
    id: "login",
    title: "Taldel — Log in",
    description:
      "Sign in to your Taldel account. Frontend login flow for clients, teams, and individuals.",
    canonicalPath: "/login",
    h1: "Welcome back to Taldel.",
    ogImage: OG_IMAGE_PATH,
    jsonLd: ["organization"],
    serviceSlug: null,
  },
  {
    id: "privacy",
    title: "Taldel — Privacy Policy",
    description:
      "How Taldel collects, uses, and protects information when you visit our site or work with our teams.",
    canonicalPath: "/privacy-policy",
    h1: "Privacy Policy",
    ogImage: OG_IMAGE_PATH,
    jsonLd: ["organization"],
    serviceSlug: null,
  },
  {
    id: "terms",
    title: "Taldel — Terms & Conditions",
    description:
      "Terms governing use of the Taldel website and how commercial engagements are framed.",
    canonicalPath: "/terms-and-conditions",
    h1: "Terms & Conditions",
    ogImage: OG_IMAGE_PATH,
    jsonLd: ["organization"],
    serviceSlug: null,
  },
];

/**
 * @param {{ page?: "home" | "service" | "product" | "product-detail" | "insights" | "insight-detail" | "signup" | "login" | "about" | "privacy" | "terms", slug?: string | null }} route
 * @returns {SeoPage}
 */
export function getSeoForRoute(route = { page: "home", slug: null }) {
  if (route.page === "insights") {
    const match = seoPages.find((p) => p.id === "insights");
    if (match) return match;
  }
  if (route.page === "insight-detail" && route.slug) {
    const detail = getInsightDetail(route.slug);
    if (detail) {
      return {
        id: `insight-${detail.slug}`,
        title: `Taldel — ${detail.title}`,
        description: detail.lead || detail.excerpt,
        canonicalPath: `/insights/${detail.slug}`,
        h1: detail.title,
        ogImage: OG_IMAGE_PATH,
        jsonLd: ["organization"],
        serviceSlug: null,
      };
    }
  }
  if (route.page === "product-detail" && route.slug) {
    const detail = getProductDetail(route.slug);
    if (detail) {
      return {
        id: `product-detail-${detail.slug}`,
        title: `Taldel — ${detail.title}`,
        description: detail.lead,
        canonicalPath: `/our-portfolios/${detail.slug}`,
        h1: detail.headline,
        ogImage: OG_IMAGE_PATH,
        jsonLd: ["organization"],
        serviceSlug: null,
      };
    }
  }
  if (route.page === "about") {
    const match = seoPages.find((p) => p.id === "about");
    if (match) return match;
  }
  if (route.page === "privacy") {
    const match = seoPages.find((p) => p.id === "privacy");
    if (match) return match;
  }
  if (route.page === "terms") {
    const match = seoPages.find((p) => p.id === "terms");
    if (match) return match;
  }
  if (route.page === "signup") {
    const match = seoPages.find((p) => p.id === "signup");
    if (match) return match;
  }
  if (route.page === "login") {
    const match = seoPages.find((p) => p.id === "login");
    if (match) return match;
  }
  if (route.page === "product") {
    const match = seoPages.find((p) => p.id === "product");
    if (match) return match;
  }
  if (route.page === "service" && route.slug) {
    const match = seoPages.find((p) => p.serviceSlug === route.slug);
    if (match) return match;
  }
  return seoPages[0];
}
